/**
 * Created by Jonas Wålekvist on 2018-01-25.
 */
import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {ResourcesGroupName} from "@nolimitcity/slot-game/bin/core/resource/ResourcesGroupName";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {DungeonQuestGameAssets} from "../DungeonQuestGameAssets";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {DungeonQuestLayerConfig} from "../../config/DungeonQuestLayerConfig";
import {DungeonQuestGameConfig, DungeonQuestGameMode} from "../DungeonQuestGameConfig";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {DungeonQuestParticleConfig} from "../../config/DungeonQuestParticleConfig";
import {TweenParticleEmitter} from "../display/TweenParticleEmitter";
import {BackgroundDividers} from "./parts/BackgroundDividers";
import {DungeonQuestHelper} from "../bigSymbols/DungeonQuestHelper";
import {Linear, TimelineLite, TweenLite} from "gsap";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";


export class BackgroundSprite extends PIXI.Container {

    public _background:PIXI.Sprite;
    public _overlay:PIXI.Sprite;

    constructor(backgroundName:string, overlayName?:string) {
        super();

        this._background = new PIXI.Sprite(GameResources.getTextures(backgroundName)[0]);
        this._background.anchor.set(0.5, 1);
        this._background.position.set(DungeonQuestGameConfig.instance.REEL_AREA_WIDTH * 0.5, DungeonQuestGameConfig.instance.REEL_AREA_HEIGHT);

        if(overlayName) {
            this._overlay = new PIXI.Sprite(GameResources.getTextures(overlayName)[0]);
        } else {
            this._overlay = new PIXI.Sprite(PIXI.Texture.EMPTY);
        }

        this._overlay.anchor.set(0.5, 1);
        this._overlay.position.set(DungeonQuestGameConfig.instance.REEL_AREA_WIDTH * 0.5, DungeonQuestGameConfig.instance.REEL_AREA_HEIGHT);
        this._overlay.blendMode = PIXI.BLEND_MODES.ADD;
        this._overlay.alpha = 0;

        this.addChild(this._background);
        this.addChild(this._overlay);
    }

    public getShowOverlay(color:number, duration:number = 0.5):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        tl.add(() => this._overlay.tint = color);
        tl.add(new TweenLite(this._overlay, duration, {alpha : 1}));
        return tl;
    }

    public getHideOverlay(duration:number = 0.5):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        tl.add(new TweenLite(this._overlay, duration, {alpha : 0}));
        return tl;
    }


    public hide() {
        this.alpha = 0;
        this.visible = false;

    }

    public show(alpha:number = 1) {
        this.alpha = alpha;
        this.visible = true;
    }
}

export class DungeonQuestRealAreaBackgroundView extends BaseView {

    private _normalBackground:BackgroundSprite;
    private _freeSpinBackground:BackgroundSprite;
    private _randomWildBackground:BackgroundSprite;

    private _layer:ScalableLayer;
    private _particleContainer:PIXI.Container;
    private _idleParticles:TweenParticleEmitter;
    private _dividers:BackgroundDividers;
    private _activeBackgroundMode:DungeonQuestGameMode;
    private _backgroundContainer:PIXI.Container;
    private _spinGlow:PIXI.Sprite;
    private _spinGlowMask:PIXI.Sprite;
    private _darkenPlate:PIXI.Graphics;
    private _darkenLayer:ScalableLayer;
    private _darkenPlateContainer:PIXI.Container;

    constructor() {
        super(ResourcesGroupName.MAIN);
        this._darkenLayer = StageManager.getLayer(DungeonQuestLayerConfig.DARKEN_PLATE.name);
        this._layer = StageManager.getLayer(DungeonQuestLayerConfig.REEL_BACKGROUND.name);
        this._layer.addChild(this);
    }


    public getShowTint(color:number, duration:number = 0.5):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        tl.add([
            this._freeSpinBackground.getShowOverlay(color, duration),
            this._normalBackground.getShowOverlay(color, duration)
        ]);
        return tl;
    }

    public getHideTint(duration:number = 0.5):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        tl.add([
            this._freeSpinBackground.getHideOverlay(duration),
            this._normalBackground.getHideOverlay(duration)
        ]);
        return tl;
    }

    public startIdleLoop():void {
        this._idleParticles.start();
    }

    public stopIdleLoop():void {
        this._idleParticles.stop();
    }

    protected onResize(resizeData:IResizeData):void {
        this.position.y = resizeData.orientation == Orientation.PORTRAIT ? 0 : 240;
        this._darkenPlateContainer.position.y = this.position.y;
    }

    protected initAnimations():void {
        this._spinGlow = new PIXI.Sprite(GameResources.getTextures(DungeonQuestGameAssets.REAL_AREA_SPIN_GLOW)[0]);
        this._spinGlow.anchor.set(0.5, 1);
        this._spinGlow.position.set(DungeonQuestGameConfig.instance.REEL_AREA_WIDTH * 0.5, DungeonQuestGameConfig.instance.REEL_AREA_HEIGHT);
        this._spinGlow.blendMode = PIXI.BLEND_MODES.ADD;

        this._spinGlowMask = new PIXI.Sprite(GameResources.getTextures(DungeonQuestGameAssets.REAL_AREA_SPIN_GLOW_MASK)[0]);
        this._spinGlowMask.anchor.set(0.5, 0);
        this._spinGlowMask.width = this._spinGlow.width;
        this._spinGlowMask.height = 512;
        this._spinGlowMask.position.set(DungeonQuestGameConfig.instance.REEL_AREA_WIDTH * 0.5, this._spinGlow.height);
        this._spinGlow.mask = this._spinGlowMask;


        this._normalBackground = new BackgroundSprite(DungeonQuestGameAssets.REAL_AREA_BACKGROUND, DungeonQuestGameAssets.REAL_AREA_BACKGROUND_OVERLAY);
        this._normalBackground.show();
        this._freeSpinBackground = new BackgroundSprite(DungeonQuestGameAssets.REAL_AREA_FREESPIN_BACKGROUND, DungeonQuestGameAssets.REAL_AREA_FREESPIN_BACKGROUND_OVERLAY);
        this._freeSpinBackground.hide();
        this._randomWildBackground = new BackgroundSprite(DungeonQuestGameAssets.REAL_AREA_RANDOM_WILD_BACKGROUND);
        this._randomWildBackground.hide();

        this._dividers = new BackgroundDividers(GameConfig.instance.REEL_AREA_WIDTH, this._normalBackground.height);

        this._particleContainer = new PIXI.Container();
        this._particleContainer.position.y = DungeonQuestGameConfig.instance.REEL_AREA_HEIGHT;
        this.createParticles();

        this._backgroundContainer = new PIXI.Container();
        this._backgroundContainer.addChild(this._normalBackground);
        this._activeBackgroundMode = DungeonQuestGameMode.NORMAL;


        this._darkenPlateContainer = new PIXI.Container();
        this._darkenPlate = new PIXI.Graphics();
        this._darkenPlate.beginFill(DungeonQuestHelper.getDarkenColor(), 0.8);
        const bgLineMargin = 8;
        const plateWidth = this._normalBackground._background.width - bgLineMargin;
        const plateHeight = this._normalBackground._background.height;
        this._darkenPlate.drawRect(
            -plateWidth * this._normalBackground._background.anchor.x,
            -plateHeight * this._normalBackground._background.anchor.y,
            plateWidth,
            plateHeight
        );
        this._darkenPlate.endFill();
        this._darkenPlate.position.set(this._normalBackground._background.position.x, this._normalBackground._background.position.y);
        this._darkenPlate.alpha = 0;

        this._darkenPlateContainer.addChild(this._darkenPlate);
        this._darkenLayer.addChild(this._darkenPlateContainer);

        this.addChild(this._backgroundContainer);
        this.addChild(this._particleContainer);
        this.addChild(this._dividers);
        this.addChild(this._spinGlow);
        this.addChild(this._spinGlowMask);

        this.startIdleLoop();
    }

    private createParticles() {
        const emberTextures:PIXI.Texture[] = [
            GameResources.getTextures(DungeonQuestGameAssets.MAIN_IDLE_PARTICLE_1)[0],
            GameResources.getTextures(DungeonQuestGameAssets.MAIN_IDLE_PARTICLE_2)[0],
            GameResources.getTextures(DungeonQuestGameAssets.MAIN_IDLE_PARTICLE_3)[0]
        ];
        this._idleParticles = new TweenParticleEmitter(emberTextures, DungeonQuestParticleConfig.idleParticleConfig);
        this._particleContainer.addChild(this._idleParticles);
    }

    private addBgOnTop(background:BackgroundSprite) {
        this._backgroundContainer.addChild(background);
    }

    public fadeInDarkenPlate(duration:number):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        tl.add(new TweenLite(this._darkenPlate, duration, {alpha : 1, ease : Linear.easeNone}));
        return tl;
    }

    public fadeOutDarkenPlate(duration:number):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        tl.add(new TweenLite(this._darkenPlate, duration, {alpha : 0, ease : Linear.easeNone}));
        return tl;
    }

    public switch(mode:DungeonQuestGameMode, duration:number = 0.5):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        if(this._activeBackgroundMode == mode) {
            return tl;
        }

        if(mode == DungeonQuestGameMode.FREESPIN) {
            tl.add(() => this.addBgOnTop(this._freeSpinBackground));
            tl.add(() => this._freeSpinBackground.show(0));
            tl.add(new TweenLite(this._freeSpinBackground, duration, {alpha : 1}));
        } else if(mode == DungeonQuestGameMode.RANDOM_WILD_TRANSFORM) {
            tl.add(() => this.addBgOnTop(this._randomWildBackground));
            tl.add(() => this._randomWildBackground.show(0));
            tl.add(new TweenLite(this._randomWildBackground, duration, {alpha : 1}));
        } else {
            if(this._activeBackgroundMode == DungeonQuestGameMode.FREESPIN) {
                tl.add(new TweenLite(this._freeSpinBackground, duration, {alpha : 0}));
            } else if(this._activeBackgroundMode == DungeonQuestGameMode.RANDOM_WILD_TRANSFORM) {
                tl.add(new TweenLite(this._randomWildBackground, duration, {alpha : 0}));
            }
        }

        this._activeBackgroundMode = mode;
        return tl;
    }

    public runSpinAnimation(duration:number) {
        const tl:TimelineLite = new TimelineLite();
        const targetYPos = -(this._spinGlow.height - DungeonQuestGameConfig.instance.REEL_AREA_HEIGHT + this._spinGlowMask.height);
        tl.add(() => this._spinGlowMask.position.set(DungeonQuestGameConfig.instance.REEL_AREA_WIDTH * 0.5, this._spinGlow.height));
        tl.add(new TweenLite(this._spinGlowMask, duration, {y : targetYPos}));


    }

    public getShowAttention(duration:number, color:number):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        tl.add([
            () => this._dividers.tint(color),
            this._dividers.showAttention(duration)
        ]);

        return tl;

    }

    public getHideAttention(duration:number):TimelineLite {
        return this._dividers.hideAttention(duration);
    }
}